import type { PublishedBrief } from 'fym-shared/src/releases/types';
import { nextLastSeen } from './whatsNewRules';

const KEY_PREFIX = 'm24WhatsNew:';

/** The build id that the person closed with Later or the X. One value for the browser. */
export const UPDATE_LATER_KEY = 'm24UpdateLater';

/** What one user keeps in this browser. */
export interface WhatsNewState {
  /** The newest version whose notes the person closed. Null: no value yet. */
  lastSeen: string | null;
  /** The last answer of `releases.get` for the running version, for a page with no connection. */
  cache: { version: string; brief: PublishedBrief } | null;
}

/** The localStorage key of one user (the pattern of `aiNudge/aiNudgeDismissed.ts`). */
export const whatsNewKey = (userId: number): string => `${KEY_PREFIX}${userId}`;

const isBrief = (value: unknown): value is PublishedBrief =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as PublishedBrief).version === 'string' &&
  Array.isArray((value as PublishedBrief).entries);

/**
 * Reads the value of one user. Null when there is no value, when the value cannot be read, or when
 * the browser blocks localStorage.
 */
export function readWhatsNewState(userId: number): WhatsNewState | null {
  try {
    const raw = window.localStorage.getItem(whatsNewKey(userId));
    if (raw === null) {
      return null;
    }
    const parsed = JSON.parse(raw) as { lastSeen?: unknown; cache?: { version?: unknown; brief?: unknown } | null };
    const lastSeen = typeof parsed.lastSeen === 'string' ? parsed.lastSeen : null;
    const cache =
      parsed.cache !== null &&
      parsed.cache !== undefined &&
      typeof parsed.cache.version === 'string' &&
      isBrief(parsed.cache.brief)
        ? { version: parsed.cache.version, brief: parsed.cache.brief }
        : null;
    return { lastSeen, cache };
  } catch {
    return null;
  }
}

function writeWhatsNewState(userId: number, state: WhatsNewState): void {
  try {
    window.localStorage.setItem(whatsNewKey(userId), JSON.stringify(state));
  } catch {
    // The value stays for this page view only.
  }
}

/** Stores the version as seen. The stored value only moves forward. Returns the new state. */
export function markWhatsNewSeen(userId: number, version: string): WhatsNewState {
  const current = readWhatsNewState(userId) ?? { lastSeen: null, cache: null };
  const next = { ...current, lastSeen: nextLastSeen(current.lastSeen, version) };
  writeWhatsNewState(userId, next);
  return next;
}

/** Keeps the last answer for the running version. Null forgets it (the version is not published). */
export function writeWhatsNewCache(userId: number, version: string, brief: PublishedBrief | null): void {
  const current = readWhatsNewState(userId) ?? { lastSeen: null, cache: null };
  writeWhatsNewState(userId, { ...current, cache: brief === null ? null : { version, brief } });
}

/** The build id that the person closed. A blocked localStorage counts as "none". */
export function readUpdateLater(): string | null {
  try {
    return window.localStorage.getItem(UPDATE_LATER_KEY);
  } catch {
    return null;
  }
}

export function writeUpdateLater(buildId: string): void {
  try {
    window.localStorage.setItem(UPDATE_LATER_KEY, buildId);
  } catch {
    // The prompt stays closed for this page view only (the caller keeps the value in state).
  }
}
